import isEqual from 'lodash/isEqual';
import React from 'react';
import { ClientWebPageQueryParams } from '@common/model/meta/clientWebPageQueryParams';
import { UpdateQueryParams } from '@cp/shared/queryParams';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { ClientWebPageProps } from '@common/model/meta/clientWebPageProps';
import { useTrackFilterEvents } from './useTrackFilterEvents';

type CatalogFilterSource = 'pageProps' | 'queryParams';

interface UseCatalogFilterReturn {
  filter: ClientWebPageProps[ClientWebPage.Catalog]['filter'] | undefined;
  filterSource: CatalogFilterSource;
  setFilter: (newFilter: ClientWebPageProps[ClientWebPage.Catalog]['filter']) => void;
}

export const useCatalogFilter = ({
  pageProps,
  queryParams,
  updateQueryParams,
}: {
  pageProps: ClientWebPageProps[ClientWebPage.Catalog];
  queryParams: ClientWebPageQueryParams<ClientWebPage.Catalog>;
  updateQueryParams: UpdateQueryParams<ClientWebPageQueryParams<ClientWebPage.Catalog>>;
}): UseCatalogFilterReturn => {
  const getCurrentFilter = React.useCallback(() => queryParams.filter /* ?? pageProps.filter */, [queryParams.filter]);

  const [filter, setCurrentFilter] = React.useState(getCurrentFilter());
  const track = useTrackFilterEvents();

  React.useEffect(() => {
    const currentFilter = getCurrentFilter();

    setCurrentFilter((prevFilter) => {
      track(currentFilter, prevFilter);
      if (!isEqual(prevFilter, currentFilter)) {
        return currentFilter;
      }
      return prevFilter;
    });
  }, [getCurrentFilter, pageProps, queryParams, track]);

  const setFilter = React.useCallback(
    (newFilter: ClientWebPageProps[ClientWebPage.Catalog]['filter']) => {
      updateQueryParams(
        { page: undefined, filter: newFilter && Object.keys(newFilter).length ? newFilter : undefined },
        { historyMethod: 'replace' },
      );
    },
    [updateQueryParams],
  );

  return {
    filter,
    filterSource: /* queryParams.filter ? */ 'queryParams' /* : 'pageProps' */,
    setFilter,
  };
};
